"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { createContext, useContext, useMemo, useState, useTransition, type ReactNode } from "react";
import { GripVertical, Plus, Save, Search, Trash2, X } from "lucide-react";
import { saveInlineEditorialSectionArticles } from "@/lib/admin-actions";
import { EditorialSectionLayout } from "@/generated/prisma/enums";
import {
  INLINE_EDITOR_HERO_SECTION_TITLE,
  type PublicEditorArticle,
  type PublicEditorSection,
} from "@/lib/public-editorial-types";
import { cn } from "@/lib/utils";

type InlineEditorContextValue = {
  enabled: boolean;
  routePath: string;
  articles: PublicEditorArticle[];
  sections: PublicEditorSection[];
  categorySlug?: string | null;
  setEnabled: (enabled: boolean) => void;
  updateSection: (section: PublicEditorSection) => void;
};

const InlineEditorContext = createContext<InlineEditorContextValue | null>(null);

export function usePublicEditorialInlineEditor() {
  return useContext(InlineEditorContext);
}

function dateLabel(value: string | null) {
  if (!value) return "Nincs publikálási dátum";
  return new Intl.DateTimeFormat("hu-HU", { year: "numeric", month: "short", day: "numeric" }).format(new Date(value));
}

function syntheticSection(title: string, articleIds: string[] = []): PublicEditorSection {
  return {
    id: "",
    title,
    subtitle: null,
    layout: EditorialSectionLayout.GRID,
    order: title === INLINE_EDITOR_HERO_SECTION_TITLE ? -100 : 0,
    articleIds,
  };
}

export function PublicEditorialInlineEditorProvider({
  routePath,
  articles,
  sections,
  categorySlug,
  toggleClassName = "bottom-4 right-4",
  children,
}: {
  routePath: string;
  articles: PublicEditorArticle[];
  sections: PublicEditorSection[];
  categorySlug?: string | null;
  toggleClassName?: string;
  children: ReactNode;
}) {
  const [enabled, setEnabled] = useState(false);
  const [localSections, setLocalSections] = useState(sections);

  function updateSection(section: PublicEditorSection) {
    setLocalSections((current) => {
      const index = current.findIndex((item) => item.id === section.id || (!item.id && item.title === section.title));
      if (index === -1) return [...current, section];
      const next = [...current];
      next[index] = section;
      return next;
    });
  }

  return (
    <InlineEditorContext.Provider
      value={{ enabled, routePath, articles, sections: localSections, categorySlug, setEnabled, updateSection }}
    >
      <div className={cn("fixed z-[90]", toggleClassName)}>
        <label className="flex cursor-pointer items-center gap-3 rounded-md border border-[#d8d2c5] bg-white px-4 py-3 text-sm font-black uppercase text-[#111111] shadow-md">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            className="h-4 w-4 accent-[#A4243B]"
          />
          Szerkesztő mód
        </label>
      </div>
      {children}
    </InlineEditorContext.Provider>
  );
}

export function EditableEditorialHeroSlot({
  manualArticleId,
  fallbackArticleId,
  children,
}: {
  manualArticleId?: string | null;
  fallbackArticleId?: string | null;
  children: ReactNode;
}) {
  const context = usePublicEditorialInlineEditor();
  if (!context?.enabled) return <>{children}</>;

  const section =
    context.sections.find((item) => item.title === INLINE_EDITOR_HERO_SECTION_TITLE) ??
    syntheticSection(INLINE_EDITOR_HERO_SECTION_TITLE, manualArticleId ? [manualArticleId] : []);
  const currentArticleId = section.articleIds[0] ?? manualArticleId ?? fallbackArticleId ?? "";

  return (
    <InlineEditorSectionShell
      key={`${section.id || section.title}:${currentArticleId}`}
      section={section}
      label="Vezető cikk"
      maxArticles={1}
      emptyHint={
        manualArticleId
          ? undefined
          : "Automatikus vezető cikk: legfrissebb publikus cikk. Válassz kézzel, ha fixálni akarod."
      }
      selectedArticleIds={currentArticleId ? [currentArticleId] : []}
    >
      {children}
    </InlineEditorSectionShell>
  );
}

export function EditableEditorialSection({
  sectionId,
  placeholderOnly = false,
  children,
}: {
  sectionId: string;
  placeholderOnly?: boolean;
  children: ReactNode;
}) {
  const context = usePublicEditorialInlineEditor();
  if (!context?.enabled) return placeholderOnly ? null : <>{children}</>;
  const section = context.sections.find((item) => item.id === sectionId);
  if (!section) return placeholderOnly ? null : <>{children}</>;

  return (
    <InlineEditorSectionShell
      key={`${section.id}:${section.articleIds.join("\0")}`}
      section={section}
      label={section.title}
      selectedArticleIds={section.articleIds}
    >
      {children}
    </InlineEditorSectionShell>
  );
}

export function AdminOnlyEditorialEmptyState({ children }: { children: ReactNode }) {
  const context = usePublicEditorialInlineEditor();
  if (!context?.enabled) return null;
  return <>{children}</>;
}

function InlineEditorSectionShell({
  section,
  label,
  maxArticles,
  selectedArticleIds,
  emptyHint,
  children,
}: {
  section: PublicEditorSection;
  label: string;
  maxArticles?: number;
  selectedArticleIds: string[];
  emptyHint?: string;
  children: ReactNode;
}) {
  const context = usePublicEditorialInlineEditor();
  const router = useRouter();
  const [articleIds, setArticleIds] = useState(selectedArticleIds);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [dragArticleId, setDragArticleId] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const articleById = useMemo(() => new Map(context?.articles.map((article) => [article.id, article])), [context?.articles]);

  if (!context) return <>{children}</>;
  const editor = context;

  function moveArticle(articleId: string, targetArticleId: string) {
    if (articleId === targetArticleId) return;
    setArticleIds((current) => {
      const next = current.filter((id) => id !== articleId);
      const targetIndex = next.indexOf(targetArticleId);
      next.splice(targetIndex < 0 ? next.length : targetIndex, 0, articleId);
      return next;
    });
  }

  function addArticle(articleId: string) {
    setArticleIds((current) => {
      const next = maxArticles === 1 ? [articleId] : [...current.filter((id) => id !== articleId), articleId];
      return typeof maxArticles === "number" ? next.slice(0, maxArticles) : next;
    });
    setPickerOpen(false);
    setMessage("Módosítás mentésre vár.");
  }

  function save(nextArticleIds = articleIds) {
    const previous = [...articleIds];
    setArticleIds(nextArticleIds);
    setMessage("Mentés...");
    startTransition(async () => {
      const formData = new FormData();
      if (section.id) formData.set("sectionId", section.id);
      formData.set("routePath", editor.routePath);
      formData.set("title", section.title);
      if (section.subtitle) formData.set("subtitle", section.subtitle);
      formData.set("layout", section.layout);
      formData.set("order", String(section.order));
      nextArticleIds.forEach((articleId) => formData.append("articleIds", articleId));
      const result = await saveInlineEditorialSectionArticles(formData);
      if (!result.ok) {
        setArticleIds(previous);
        setMessage(result.message);
        return;
      }
      const updatedSection = {
        ...section,
        id: result.data?.sectionId ?? section.id,
        articleIds: nextArticleIds,
      };
      editor.updateSection(updatedSection);
      setMessage(result.message ?? "Mentve.");
      router.refresh();
    });
  }

  return (
    <div className="relative rounded-md outline outline-1 outline-dashed outline-offset-4 outline-[#A97800]/70">
      <div className="absolute right-1 top-1 z-[80] flex max-w-[calc(100%-0.5rem)] flex-wrap items-center justify-end gap-1.5">
        <span className="rounded border border-[#d8d2c5] bg-white px-2 py-1 text-[10px] font-black uppercase text-[#111111] shadow-sm">
          {label}
        </span>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="inline-flex min-h-7 items-center gap-1 rounded border border-[#d8d2c5] bg-white px-2 py-1 text-[10px] font-black uppercase text-[#111111] shadow-sm hover:border-[#A4243B]"
        >
          <Plus size={13} /> {maxArticles === 1 ? "Cikk cseréje" : "Cikk hozzáadása"}
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={() => save()}
          className="inline-flex min-h-7 items-center gap-1 rounded bg-[#A4243B] px-2 py-1 text-[10px] font-black uppercase text-white shadow-sm disabled:cursor-wait disabled:opacity-60"
        >
          <Save size={13} /> {pending ? "Mentés..." : "Mentés"}
        </button>
      </div>
      <div className="absolute left-1 top-1 z-[80] max-w-[min(24rem,calc(100%-0.5rem))] rounded border border-[#d8d2c5] bg-white/95 px-2 py-1 text-[10px] font-semibold text-[#6B6B6B] shadow-sm">
        {message ?? emptyHint ?? "Húzással rendezhető. A mentés után frissül az oldal."}
      </div>
      <div className="pt-9">{children}</div>
      <div className="mt-3 grid gap-2 rounded-md border border-[#d8d2c5] bg-white/95 p-2 shadow-sm">
        {articleIds.length ? (
          articleIds.map((articleId) => {
            const article = articleById.get(articleId);
            if (!article) return null;
            return (
              <div
                key={articleId}
                draggable
                onDragStart={() => setDragArticleId(articleId)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => {
                  if (dragArticleId) moveArticle(dragArticleId, articleId);
                  setDragArticleId(null);
                  setMessage("Módosítás mentésre vár.");
                }}
                className="grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded border border-zinc-200 bg-[#FCFBF8] px-2 py-2 text-sm"
              >
                <GripVertical size={16} className="text-zinc-400" />
                <div className="min-w-0">
                  <p className="truncate font-black text-[#111111]">{article.title}</p>
                  <p className="text-xs font-semibold text-zinc-500">
                    {article.categoryName} · {article.status} · {dateLabel(article.publishedAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const next = articleIds.filter((id) => id !== articleId);
                    setArticleIds(next);
                    setMessage("Módosítás mentésre vár.");
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded border border-zinc-200 text-[#A4243B] hover:bg-[#A4243B] hover:text-white"
                  aria-label="Eltávolítás"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })
        ) : (
          <p className="rounded border border-dashed border-zinc-200 px-3 py-3 text-sm font-semibold text-zinc-500">
            Nincs kézzel kiválasztott cikk.
          </p>
        )}
      </div>
      <ArticlePicker
        open={pickerOpen}
        articles={context.articles}
        currentArticleIds={articleIds}
        categorySlug={context.categorySlug}
        onClose={() => setPickerOpen(false)}
        onSelect={addArticle}
      />
    </div>
  );
}

function ArticlePicker({
  open,
  articles,
  currentArticleIds,
  categorySlug,
  onClose,
  onSelect,
}: {
  open: boolean;
  articles: PublicEditorArticle[];
  currentArticleIds: string[];
  categorySlug?: string | null;
  onClose: () => void;
  onSelect: (articleId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<"current" | "all">(categorySlug ? "current" : "all");
  const selectedIds = new Set(currentArticleIds);
  const visibleArticles = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("hu-HU");
    return articles
      .filter((article) => (scope === "all" || !categorySlug ? true : article.leagueSlugs.includes(categorySlug)))
      .filter((article) =>
        `${article.title} ${article.categoryName}`.toLocaleLowerCase("hu-HU").includes(normalized),
      )
      .slice(0, 80);
  }, [articles, categorySlug, query, scope]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="ml-auto flex h-full w-full max-w-2xl flex-col rounded-md bg-white shadow-2xl">
        <div className="border-b border-zinc-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-black uppercase text-[#111111]">Cikk kiválasztása</h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded border border-zinc-200 hover:bg-zinc-100"
              aria-label="Bezárás"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
            <label className="relative block">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Keresés cím vagy kategória alapján"
                className="h-11 w-full rounded-md border border-zinc-300 pl-9 pr-3 text-sm"
              />
            </label>
            {categorySlug ? (
              <div className="inline-grid grid-cols-2 rounded-md border border-zinc-200 p-1 text-xs font-black uppercase">
                <button
                  type="button"
                  onClick={() => setScope("current")}
                  className={cn("rounded px-3 py-2", scope === "current" && "bg-[#111111] text-white")}
                >
                  Oldal
                </button>
                <button
                  type="button"
                  onClick={() => setScope("all")}
                  className={cn("rounded px-3 py-2", scope === "all" && "bg-[#111111] text-white")}
                >
                  Összes
                </button>
              </div>
            ) : null}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="grid gap-2">
            {visibleArticles.map((article) => (
              <button
                key={article.id}
                type="button"
                onClick={() => onSelect(article.id)}
                className="grid grid-cols-[72px_1fr_auto] items-center gap-3 rounded-md border border-zinc-200 p-2 text-left hover:border-[#A4243B] hover:bg-[#FCFBF8]"
              >
                <span className="relative block aspect-[4/3] overflow-hidden rounded bg-zinc-100">
                  {article.coverImage ? (
                    <Image src={article.coverImage} alt="" fill sizes="72px" className="object-cover" />
                  ) : null}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-black text-[#111111]">{article.title}</span>
                  <span className="mt-1 block text-xs font-semibold text-zinc-500">
                    {article.categoryName} · {article.status} · {dateLabel(article.publishedAt)}
                  </span>
                </span>
                <span className="text-xs font-black uppercase text-[#A4243B]">
                  {selectedIds.has(article.id) ? "Kiválasztva" : "Választás"}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
